import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CategoryDetailsPageContext } from "@insite/content-library/Pages/CategoryDetailsPage";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasCategoryContext, withCategory } from "@SupplyLand/Overrides/Components/CategoryContext";
import * as React from "react";
import { connect, MapStateToProps } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

type Props = WidgetProps & HasCategoryContext & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: { category: Category; products: ProductDto[] }) => {
    const subCategories: Readonly<Category>[] = [];
    ownProps.category?.subCategoryIds?.forEach(subCategoryId => {
        const cat = getCategoryState(state, subCategoryId).value;
        if (cat !== undefined) {
            subCategories.push(cat);
        }
    });
    return {
        mainCategory: ownProps.category,
        products: ownProps.products,
        subCategories,
    };
};

export interface MultiPurposeItemStyles {
    container?: GridContainerProps;
    heroContainer?: GridContainerProps;
    productCategoryContainer?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    heroSubCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    heroInnerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    heroSubCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    heroSubCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    heroSubCategoryNameLink?: LinkPresentationProps;
}

export const multiPurposeItemStyles: MultiPurposeItemStyles = {
    heroContainer: {
        gap: 12,
        offsetCss: css`
            align-items: stretch;
        `,
    },
    productCategoryContainer: {
        gap: 12,
        offsetCss: css`
            align-items: baseline;
        `,
    },
    heroSubCategoryItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            margin-bottom: 30px !important;
            //margin: 0px -2px 30px -2px !important;
        `,
    },
    heroInnerContainer: {
        gap: 0,
        css: css`
            justify-content: center;
        `,
    },
    subCategoryImageItem: {
        width: 12,
        css: css`
            width: 100%;
            justify-content: center;
        `,
    },
    heroSubCategoryImageClickable: {
        css: css`
            justify-content: center;
            display: flex;
            flex-direction: column;
            width: 100%;
            & :hover {
                box-shadow: 0px 3px 15px ${getColor("primary")};
            }
        `,
    },
    heroSubCategoryNameLinkItem: {
        width: [10, 10, 12, 12, 12],
        css: css`
            padding-top: 4px;
            padding-bottom: 4px;
            background: black;
            width: 100%;
            text-transform: uppercase;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                ])}
        `,
    },
    heroSubCategoryNameLink: {
        css: css`
            width: 100%;
            color: white;
        `,
        color: "white",
        typographyProps: {
            weight: "bold",
            css: css`
                width: 100%;
                text-align: center;
                word-wrap: break-word;
                color: white;
            `,
        },
    },
    container: {
        gap: 0,
        offsetCss: css`
            align-items: stretch;
            margin: -6px;
        `,
    },
    subCategoryItem: {
        width: [12, 12, 4, 4, 3],
        css: css`
            align-self: stretch;
            align-items: stretch;
        `,
    },
    innerContainer: {
        css: css``,
        gap: 0,
    },
    subCategoryImageClickable: {
        css: css`
            padding: 20px 20px 20px 21px;
            background-color: whitesmoke;
            justify-content: center;
            display: flex;
            height: 100%;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 8px 8px 8px 8px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            & :hover {
                box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [10, 10, 12, 12, 12],
        css: css`
            padding-bottom: 8px;
            align-items: flex-end;
            color: white;
            margin: auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                ])}
        `,
    },
    subCategoryNameLink: {
        css: css`
            width: 100%;
            text-align: center;
            word-wrap: break-word;
            font-weight: bold;
        `,
        color: "text.main",
    },
};

export interface MultiPurposeItem {
    name?: string;
    imagePath?: string;
    url?: string;
    altText?: string;
    isProduct: boolean;
    sortOrder: number;
    id: string;
}

export const MultiPurposeItemView = ({ product, showImages }: ReturnType<any>) => {
    if (!product) {
        return null;
    }
    const styles = multiPurposeItemStyles;
    return (
        <Clickable href={product.url} {...styles.subCategoryImageClickable}>
            <GridItem {...styles.subCategoryImageItem}>
                {product.imagePath && (
                    <LazyImage src={product.imagePath} altText={product.altText} {...styles.subCategoryImage} />
                )}
            </GridItem>
            <GridItem {...styles.subCategoryNameLinkItem}>
                <Typography {...styles.subCategoryNameLink} data-test-selector={`productListProductCard${product.id}`}>
                    {product.name}
                </Typography>
            </GridItem>
        </Clickable>
    );
};
